<script lang="ts">
  import { X, Mic, PhoneOff, Clock } from 'lucide-svelte';
  import { _ } from 'svelte-i18n';
  import Card from '../ui/card.svelte';
  import Button from '../ui/button.svelte';
  import { supabase } from '../../lib/supabase';
  import { selectedTargetLanguage } from '../../stores/flashcards';
  import type { Database } from '../../integrations/supabase/types';

  type Category = Database['public']['Tables']['flashcard_categories']['Row'];
  type Level = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

  const props = $props<{
    open: boolean;
    category: Category;
    level: Level;
    onClose: () => void;
  }>();

  let sessionId = $state<string | null>(null);
  let signedUrl = $state<string | null>(null);
  let isStarting = $state(false);
  let isEnding = $state(false);
  let isActive = $state(false);
  let remainingSeconds = $state(120);
  let timerInterval: number | null = null;
  let conversationConnection: any = null;

  async function startConversation() {
    if (!$selectedTargetLanguage) {
      alert($_('speak.select_language_first'));
      return;
    }

    isStarting = true;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error($_('speak.not_authenticated'));
      }

      const response = await fetch('/api/dialogue/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          level: props.level,
          topic: props.category.id,
          languageId: $selectedTargetLanguage.id,
        }),
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMessage = $_('speak.failed_to_start');

        try {
          if (contentType?.includes('application/json')) {
            const error = await response.json();
            errorMessage = error.error || error.message || errorMessage;
          } else {
            errorMessage = await response.text();
          }
        } catch {
          errorMessage = `${$_('speak.server_error')} (${response.status}): ${response.statusText}`;
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();
      sessionId = data.sessionId;
      signedUrl = data.signedUrl;

      // Connect to ElevenLabs
      await connectToElevenLabs(signedUrl);

      // Switch to active state
      isActive = true;

      // Start timer
      startTimer();
    } catch (error) {
      console.error('Error starting conversation:', error);
      alert(error instanceof Error ? error.message : $_('speak.failed_to_start'));
      props.onClose();
    } finally {
      isStarting = false;
    }
  }

  async function connectToElevenLabs(url: string) {
    console.log('🎤 Connecting to ElevenLabs...');

    if (!url) {
      throw new Error($_('speak.no_url'));
    }

    try {
      // TODO: Implement actual ElevenLabs Conversational AI connection
      // This is a placeholder for the actual implementation
      console.log('✅ ElevenLabs connection ready (mock mode)');
    } catch (error) {
      console.error('❌ Failed to connect to ElevenLabs:', error);
      throw error;
    }
  }

  function startTimer() {
    remainingSeconds = 120;
    timerInterval = window.setInterval(() => {
      remainingSeconds--;
      if (remainingSeconds <= 0) {
        endConversation();
      }
    }, 1000);
  }

  async function endConversation() {
    if (!sessionId) return;

    // Stop timer
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }

    // Disconnect
    if (conversationConnection) {
      conversationConnection = null;
    }

    isEnding = true;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error($_('speak.not_authenticated'));
      }

      const response = await fetch('/api/dialogue/end', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          sessionId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || $_('speak.failed_to_end'));
      }

      // Close dialog
      props.onClose();
    } catch (error) {
      console.error('Error ending conversation:', error);
      alert(error instanceof Error ? error.message : $_('speak.failed_to_end'));
    } finally {
      isEnding = false;
    }
  }

  function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget && !isActive) {
      props.onClose();
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && !isActive) {
      props.onClose();
    }
  }

  // Auto-start when dialog opens
  $effect(() => {
    if (props.open && !isActive && !isStarting) {
      startConversation();
    }
  });
</script>

{#if props.open}
  <div
    class="fixed inset-0 z-[96] flex flex-col bg-background/95 backdrop-blur"
    role="dialog"
    aria-modal="true"
    tabindex="0"
    onkeydown={handleKeydown}
    onclick={handleBackdropClick}
  >
    <button
      type="button"
      class="btn btn-close dialog-close--fixed"
      onclick={props.onClose}
      aria-label={$_('aria.close')}
      disabled={isActive}
    >
      <X class="h-5 w-5" />
    </button>

    <div class="h-full w-full overflow-y-auto px-4 py-10">
      <div class="mx-auto w-full max-w-2xl space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
        <header class="space-y-1">
          <p class="text-xs uppercase tracking-[0.12em] text-slate-500">{$_('speak.conversation')}</p>
          <h2 class="text-2xl font-bold text-slate-900">{props.category.name}</h2>
          <p class="text-sm text-slate-600">
            {$_('speak.level')}: {props.level}
          </p>
        </header>

        {#if isStarting}
          <!-- Starting State -->
          <Card class="p-8 text-center">
            <div class="flex flex-col items-center gap-4">
              <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p class="text-lg font-medium">{$_('speak.starting')}</p>
              <p class="text-sm text-muted-foreground">{$_('speak.connecting')}</p>
            </div>
          </Card>
        {:else if isActive}
          <!-- Active Call -->
          <Card class="p-8">
            <!-- Timer -->
            <div class="text-center mb-8">
              <div class={`text-6xl font-bold mb-2 ${remainingSeconds <= 10 ? 'text-red-500' : 'text-foreground'}`}>
                {formatTime(remainingSeconds)}
              </div>
              <p class="text-muted-foreground">{$_('speak.time_remaining')}</p>
            </div>

            <!-- Avatar Animation -->
            <div class="flex justify-center mb-8">
              <div class="relative">
                <div class="w-32 h-32 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
                  <Mic class="h-16 w-16 text-white animate-pulse" />
                </div>
                <div class="absolute inset-0 rounded-full bg-primary/20 animate-ping"></div>
              </div>
            </div>

            <!-- Status -->
            <div class="text-center mb-8">
              <p class="text-lg font-medium">{$_('speak.listening')}</p>
              <p class="text-sm text-muted-foreground mt-1">{$_('speak.speak_naturally')}</p>
            </div>

            <!-- End Button -->
            <div class="flex justify-center">
              <Button
                variant="destructive"
                size="lg"
                onclick={endConversation}
                disabled={isEnding}
                class="px-8"
              >
                {#if isEnding}
                  <div class="flex items-center gap-2">
                    <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>{$_('speak.ending')}</span>
                  </div>
                {:else}
                  <div class="flex items-center gap-2">
                    <PhoneOff class="h-5 w-5" />
                    <span>{$_('speak.end_call')}</span>
                  </div>
                {/if}
              </Button>
            </div>
          </Card>
        {/if}
      </div>
    </div>
  </div>
{/if}
